/**
 * Formato copiable para depuración (soporte / IA). Sin secretos en cabeceras.
 */

const MAX_BLOCK = 6000;

function truncate(s, max = MAX_BLOCK) {
  if (s == null) return '';
  const t = String(s);
  return t.length > max ? `${t.slice(0, max)}\n…[truncado ${t.length - max} caracteres]` : t;
}

function safeJson(value) {
  if (value == null) return '';
  if (typeof value === 'string') return truncate(value);
  try {
    return truncate(JSON.stringify(value, null, 2));
  } catch {
    return truncate(String(value));
  }
}

function serializeResponseBody(data) {
  if (data == null) return '(vacío)';
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return `[Blob type=${data.type || '—'} size=${data.size}]`;
  }
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
    return `[ArrayBuffer ${data.byteLength} bytes]`;
  }
  return safeJson(data);
}

function serializeRequestBody(data) {
  if (data === undefined) return '(sin body)';
  if (data === null) return '(null)';
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const keys = [];
    try {
      data.forEach((_v, k) => {
        if (!keys.includes(k)) keys.push(k);
      });
    } catch {
      return '[FormData: no se pudo leer]';
    }
    return keys.length ? `[FormData] campos: ${keys.join(', ')}` : '[FormData vacío]';
  }
  return safeJson(data) || '(vacío)';
}

function redactHeaders(raw) {
  if (!raw || typeof raw !== 'object') return {};
  try {
    const out = {};
    const entries = typeof raw.toJSON === 'function' ? Object.entries(raw.toJSON()) : Object.entries({ ...raw });
    for (const [k, v] of entries) {
      const key = String(k);
      const lower = key.toLowerCase();
      if (lower === 'authorization') {
        out[key] = 'Bearer <omitido>';
      } else if (lower === 'shp' || lower === 'cookie' || lower === 'set-cookie') {
        out[key] = '<omitido>';
      } else {
        out[key] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function routeBlock(route) {
  if (!route) return '(sin router)';
  const name = route.name != null ? String(route.name) : '—';
  return [
    `Nombre ruta: ${name}`,
    `path: ${route.path || '—'}`,
    `fullPath: ${route.fullPath || '—'}`,
  ].join('\n');
}

const BANNER = '===== KOSEXA Tienda · cuaderno de errores =====';

export function buildPasteBodyFromAxios(error, route) {
  const cfg = error?.config || {};
  const method = (cfg.method || 'GET').toUpperCase();
  const url = cfg.baseURL ? `${cfg.baseURL}${cfg.url || ''}` : cfg.url || '—';
  const status = error.response?.status;
  const statusText = error.response?.statusText;
  const data = error.response?.data;
  const headersSent = redactHeaders(cfg.headers);
  const reqData = serializeRequestBody(cfg.data);
  const params = cfg.params != null && Object.keys(cfg.params).length ? safeJson(cfg.params) : '(sin query params)';

  const lines = [
    BANNER,
    `Fecha (ISO): ${new Date().toISOString()}`,
    '',
    '--- Contexto en la app ---',
    routeBlock(route),
    '',
    '--- Petición HTTP (Axios) ---',
    `Método: ${method}`,
    `URL: ${url}`,
    'Params:',
    params,
    '',
    'Body enviado:',
    reqData || '(vacío)',
    '',
    'Cabeceras enviadas (sensible omitido):',
    safeJson(headersSent),
    '',
    '--- Respuesta ---',
  ];

  if (error.response) {
    lines.push(`HTTP: ${status} ${statusText || ''}`.trim());
    lines.push('Cuerpo respuesta:');
    lines.push(serializeResponseBody(data));
    lines.push('');
    lines.push('Cabeceras respuesta (resumen):');
    lines.push(safeJson(redactHeaders(error.response.headers)));
  } else {
    lines.push('Sin respuesta del servidor (red / CORS / timeout / offline).');
    lines.push(`code: ${error.code || '—'}`);
    lines.push(`message: ${error.message || '—'}`);
  }

  lines.push('');
  lines.push('--- Stack (Axios / Error) ---');
  lines.push(truncate(error.stack || '(sin stack)', 4000));

  return lines.join('\n');
}

export function buildPasteBodyFromVueError(err, info, route) {
  const name = err?.name || 'Error';
  const msg = err?.message || String(err);
  const lines = [
    BANNER,
    `Fecha (ISO): ${new Date().toISOString()}`,
    'Tipo: error de Vue (render / setup / watcher)',
    '',
    '--- Contexto en la app ---',
    routeBlock(route),
    '',
    '--- Detalle ---',
    `info: ${info || '—'}`,
    `${name}: ${msg}`,
    '',
    '--- Stack ---',
    truncate(err?.stack || '(sin stack)', 6000),
  ];
  return lines.join('\n');
}

export function buildPasteBodyFromRejection(reason, route) {
  const lines = [
    BANNER,
    `Fecha (ISO): ${new Date().toISOString()}`,
    'Tipo: promesa no manejada (unhandledrejection)',
    '',
    '--- Contexto en la app ---',
    routeBlock(route),
    '',
    '--- Razón ---',
  ];
  if (reason instanceof Error) {
    lines.push(`${reason.name}: ${reason.message}`);
    lines.push('');
    lines.push('--- Stack ---');
    lines.push(truncate(reason.stack || '', 6000));
  } else {
    lines.push(safeJson(reason));
  }
  return lines.join('\n');
}

export function axiosEntrySummary(error) {
  const cfg = error?.config || {};
  const m = (cfg.method || 'get').toUpperCase();
  const u = cfg.url || '—';
  if (error.response) {
    return `${m} ${u} → HTTP ${error.response.status}`;
  }
  return `${m} ${u} → ${error.code || 'ERROR'}`;
}

export function buildPasteBodyFromWindowError(ev, route) {
  const msg = ev?.message || 'Error en ventana';
  const file = ev?.filename || '—';
  const line = ev?.lineno ?? '—';
  const col = ev?.colno ?? '—';
  const err = ev?.error;
  const lines = [
    BANNER,
    `Fecha (ISO): ${new Date().toISOString()}`,
    'Tipo: window (error global)',
    '',
    '--- Contexto en la app ---',
    routeBlock(route),
    '',
    '--- Detalle ---',
    `message: ${msg}`,
    `at: ${file}:${line}:${col}`,
    '',
    '--- Stack ---',
    truncate(err?.stack || '(sin stack Error)', 6000),
  ];
  return lines.join('\n');
}

export function buildPasteBodyFromRouterError(err, route) {
  const msg = err?.message || String(err);
  const lines = [
    BANNER,
    `Fecha (ISO): ${new Date().toISOString()}`,
    'Tipo: error de navegación (vue-router)',
    '',
    '--- Contexto en la app ---',
    routeBlock(route),
    '',
    '--- Detalle ---',
    msg,
    '',
    '--- Stack ---',
    truncate(err?.stack || '', 4000),
  ];
  return lines.join('\n');
}

export function buildMarkdownAgentReport(entries, route) {
  const href = typeof window !== 'undefined' ? window.location?.href || '' : '';
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const mode =
    typeof process !== 'undefined' && process.env && process.env.NODE_ENV ? process.env.NODE_ENV : '';
  const rname = route?.name != null ? String(route.name) : '—';
  const rpath = route?.fullPath || route?.path || '—';
  const list = Array.isArray(entries) ? [...entries].reverse() : [];

  const lines = [
    '# Informe de errores — KOSEXA Tienda (front)',
    '',
    'Bloque generado desde el cuaderno de la app. **Revise credenciales y datos sensibles** antes de compartir fuera del equipo.',
    '',
    '## Contexto',
    '',
    `- **URL:** \`${href.replace(/`/g, "'")}\``,
    `- **Ruta SPA actual:** \`${String(rpath).replace(/`/g, "'")}\` (${rname})`,
    `- **Entradas (orden antiguo → reciente):** ${list.length}`,
    `- **Generado (ISO):** ${new Date().toISOString()}`,
  ];
  if (ua) {
    lines.push(
      `- **User-Agent:** \`${ua.slice(0, 400).replace(/`/g, "'")}${ua.length > 400 ? '…' : ''}\``
    );
  }
  if (mode) lines.push(`- **NODE_ENV:** ${mode}`);
  lines.push('', '## Registro', '');

  if (!list.length) {
    lines.push('_(No hay entradas.)_', '');
  } else {
    list.forEach((e, i) => {
      lines.push(`### ${i + 1}. \`[${e.kind}]\` ${e.ts}`, '', `**Resumen:** ${e.summary || '—'}`, '', '```', String(e.body || ''), '```', '', '---', '');
    });
  }

  lines.push(
    '## Qué puede hacer el agente programador',
    '',
    '1. Reproducir o localizar en código cada fallo.',
    '2. Si es HTTP: revisar endpoint, payload y respuesta.',
    '3. Proponer parche o mitigación concreta.',
    ''
  );

  return lines.join('\n');
}
